import apiClient from '@shared/api'
import type IReporter from '@shared/api/visits/resources/admin/reporter'
import type IReporterRole from '@shared/api/visits/resources/admin/reporter-role'
import type IVisitFilterParams from '@shared/api/visits/resources/admin/visit-filter-params'
import type { IVisit, IPaginatedVisits } from '@shared/api/visits/resources/admin/visit'
import type { EVisitStatus } from '@shared/api/visits/resources/admin/constants/visit-status'
import type IVisitHistory from '@shared/api/visits/resources/admin/visit-history'
import type { RouteParamValue } from 'vue-router'
import type IMedicalDeviceList from '@shared/api/visits/resources/admin/medical-device-list'
import type IVisitResult from '@shared/api/visits/resources/admin/visit-result'
import type ISessionByToken from '@shared/api/visits/resources/admin/session-by-token'
import type IVisitFamilyParticipant from '@shared/api/visits/resources/admin/visit-family-participant'
import type IParticipant from '@/shared/types/visit/participant'
import type { EReporterRoleType } from '@shared/api/visits/resources/admin/constants/reporter-roles'

export const getVisitList = (filterParams?: IVisitFilterParams): Promise<IVisit[] | IPaginatedVisits> => {
  const params: IVisitFilterParams | null = filterParams
    ? {
        pagination: filterParams.pagination,
        page: filterParams.page,
        pageSize: filterParams.pageSize,
        startDate: filterParams.startDate,
        endDate: filterParams.endDate,
        organizationIds: filterParams.organizationIds,
        communityIds: filterParams.communityIds,
        patientIds: filterParams.patientIds,
        visitStatuses: filterParams.visitStatuses,
        taskTypes: filterParams.taskTypes,
        familyInvolved: filterParams.familyInvolved,
        sortBy: filterParams.sortBy,

        // TODO: (Deprecated) Remove in the future
        patientId: filterParams.patientId,
        organizationId: !filterParams?.communityId ? filterParams.organizationId : '',
        communityId: filterParams.communityId,
        lessThan: filterParams.lessThan,
        take: filterParams.take,
        status: filterParams.status,
      }
    : null
  return apiClient.get('/visits', { params })
}

export const getVisit = ({ visitId }: { visitId: RouteParamValue | RouteParamValue[] }): Promise<IVisit> => {
  return apiClient.get(`/visits/${visitId}`)
}

export const getPossibleVisitList = ({
  visitId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
}): Promise<IVisit[]> => {
  return apiClient.get(`/visits/${visitId}/possible-followups`)
}

export const getVisitResults = ({
  visitId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
}): Promise<IVisitResult> => {
  return apiClient.get(`/visits/${visitId}/results`)
}

export const updateVisitResults = ({
  visitId,
  payload,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  payload: IVisitResult
}): Promise<IVisitResult> => {
  return apiClient.put(`/visits/${visitId}/results`, payload)
}

export const updateVisitStatus = ({
  visitId,
  visitStatus,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  visitStatus: EVisitStatus
}) => {
  return apiClient.put(`/visits/${visitId}/statuses`, { visitStatus })
}

export const getReporterRoles = (): Promise<EReporterRoleType[]> => {
  return apiClient.get('/visits/reporter-roles')
}

export const updateReporterRoles = ({
  visitId,
  payload,
}: {
  visitId: string | string[]
  payload: IReporterRole
}): Promise<IReporterRole> => {
  return apiClient.put(`/visits/${visitId}/reporter-roles`, payload)
}

export const updateReporter = ({
  visitId,
  payload,
}: {
  visitId: string | string[]
  payload: IReporter
}): Promise<IReporter> => {
  return apiClient.put(`/visits/${visitId}/reporters`, payload)
}

export const getMedicalDeviceList = (): Promise<IMedicalDeviceList> => {
  return apiClient.get('/visits/medical-devices')
}

export const getSessionByInviteToken = ({
  token,
}: {
  token: RouteParamValue | RouteParamValue[]
}): Promise<ISessionByToken> => {
  return apiClient.get(`/visits/redirects/${token}`)
}

export const getVisitHistoryList = ({
  visitId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
}): Promise<IVisitHistory[]> => {
  return apiClient.get(`/visits/${visitId}/events`)
}

export const getParticipantList = ({
  visitId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
}): Promise<IVisitFamilyParticipant[]> => {
  return apiClient.get(`/visits/${visitId}/participants`)
}

export const createParticipant = ({
  visitId,
  payload,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  payload: IVisitFamilyParticipant
}): Promise<IParticipant> => {
  return apiClient.post(`/visits/${visitId}/participants`, payload)
}

export const deleteParticipant = ({
  visitId,
  contactId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  contactId: string
}) => {
  return apiClient.delete(`/visits/${visitId}/participants/${contactId}`)
}

export const sendVisitInvitationLink = ({
  visitId,
  data,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  data: {
    email: string
    phoneNumber: string | null
  }
}) => {
  return apiClient.put(`/visits/${visitId}/invitations`, data)
}

export const updatePatientForVisit = ({ visitId, patientId }: { visitId: string | string[]; patientId: string }) => {
  return apiClient.put(`/visits/${visitId}/patients`, { patientId })
}

export const updateVisitCommunity = ({
  visitId,
  communityId,
}: {
  visitId: string | string[]
  communityId?: string
}): Promise<IVisit> => {
  return apiClient.put(`/visits/${visitId}/communities`, null, { params: { communityId } })
}

export const deleteVisit = ({ visitId }: { visitId: RouteParamValue | RouteParamValue[] }) => {
  return apiClient.delete(`/visits/${visitId}`)
}
